import React, { useState } from 'react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Label } from './ui/label';
import { toast } from 'sonner';
import { Loader2, Send, ArrowRight, ArrowLeft, CheckCircle2, Package, BarChart3, ClipboardCheck, User, Building2 } from 'lucide-react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STEPS = [
  { id: 1, title: 'Product', icon: Package, label: 'What are you sourcing?' },
  { id: 2, title: 'Volume', icon: BarChart3, label: 'Order details' },
  { id: 3, title: 'Services', icon: ClipboardCheck, label: 'What do you need?' },
  { id: 4, title: 'Contact', icon: User, label: 'Your details' },
];

const CATEGORIES = [
  'Electronics & Components', 'Apparel & Textiles', 'Building Materials',
  'Machinery & Equipment', 'FMCG & Retail', 'Automobile Parts',
  'Consumer Goods', 'Other'
];

const VOLUMES = [
  { label: '1 – 100 units', value: '1-100 units' },
  { label: '100 – 1,000 units', value: '100-1,000 units' },
  { label: '1,000 – 10,000 units', value: '1,000-10,000 units' },
  { label: '10,000+ units', value: '10,000+ units' },
  { label: "I'm not sure yet", value: 'Not sure yet' },
];

const SERVICES = [
  { id: 'sourcing', label: 'Product Sourcing', desc: 'Find verified manufacturers' },
  { id: 'audit', label: 'Factory Audit', desc: 'Verify supplier credibility' },
  { id: 'quality', label: 'Quality Inspection', desc: 'Pre-shipment checks' },
  { id: 'shipping', label: 'Shipping & Logistics', desc: 'Door-to-door delivery' },
  { id: 'warehousing', label: 'Warehousing', desc: 'Storage solutions in China' },
  { id: 'all', label: 'Full Supply Chain', desc: 'End-to-end solution' },
];

const QuoteForm = () => {
  const [step, setStep] = useState(1);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [form, setForm] = useState({
    productCategory: '',
    customCategory: '',
    orderVolume: '',
    services: [],
    companyName: '',
    contactName: '',
    email: '',
    phone: '',
    country: '',
    message: ''
  });

  const set = (k, v) => setForm(prev => ({ ...prev, [k]: v }));

  const toggleService = (id) => {
    setForm(prev => ({
      ...prev,
      services: prev.services.includes(id)
        ? prev.services.filter(s => s !== id)
        : [...prev.services, id]
    }));
  };

  const canNext = () => {
    if (step === 1) return form.productCategory !== '';
    if (step === 2) return form.orderVolume !== '';
    if (step === 3) return form.services.length > 0;
    if (step === 4) return form.contactName && form.email && form.phone;
    return false;
  };

  const handleSubmit = async () => {
    setIsSubmitting(true);
    try {
      const payload = {
        companyName: form.companyName,
        contactName: form.contactName,
        email: form.email,
        phone: form.phone,
        country: form.country,
        serviceType: form.services.join(', '),
        productCategory: form.productCategory === 'Other' ? form.customCategory : form.productCategory,
        message: `Order Volume: ${form.orderVolume}\nServices: ${form.services.join(', ')}\n\n${form.message}`
      };
      const response = await axios.post(`${API}/quote-request`, payload);
      if (response.data.success) {
        setSubmitted(true);
        toast.success('Quote Request Submitted!', {
          description: 'Our team will contact you within 24 hours.'
        });
      }
    } catch (error) {
      toast.error('Submission Failed', {
        description: error.response?.data?.detail || 'Please try again or contact us directly.'
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  if (submitted) {
    return (
      <section id="quote" className="py-20 bg-gradient-to-b from-gray-50 to-white">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-2xl mx-auto text-center">
            <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
              <CheckCircle2 className="w-10 h-10 text-green-600" />
            </div>
            <h2 className="text-3xl md:text-4xl font-bold text-blue-900 mb-4">Quote Request Received!</h2>
            <p className="text-lg text-gray-600 mb-8">Thank you for your inquiry. Our sourcing team will review your requirements and get back to you within 24 hours.</p>
            <Button onClick={() => { setSubmitted(false); setStep(1); setForm({ productCategory: '', customCategory: '', orderVolume: '', services: [], companyName: '', contactName: '', email: '', phone: '', country: '', message: '' }); }}
              className="bg-blue-900 hover:bg-blue-800 text-white px-8 py-3"
              data-testid="submit-another-btn"
            >
              Submit Another Request
            </Button>
          </div>
        </div>
      </section>
    );
  }

  return (
    <section id="quote" className="py-20 bg-gradient-to-b from-gray-50 to-white">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8">
        <div className="max-w-3xl mx-auto">
          <div className="text-center mb-10">
            <h2 className="text-4xl md:text-5xl font-bold text-blue-900 mb-4">Get Your Free Quote</h2>
            <p className="text-lg text-gray-600">Answer a few quick questions and we'll prepare a custom sourcing plan for you</p>
          </div>

          {/* Progress Bar */}
          <div className="flex items-center justify-between mb-10 px-4">
            {STEPS.map((s, i) => {
              const Icon = s.icon;
              const isActive = step === s.id;
              const isDone = step > s.id;
              return (
                <React.Fragment key={s.id}>
                  <div className="flex flex-col items-center">
                    <div className={`w-12 h-12 rounded-full flex items-center justify-center transition-all duration-300 ${isDone ? 'bg-green-500 text-white' : isActive ? 'bg-orange-600 text-white scale-110 shadow-lg shadow-orange-200' : 'bg-gray-200 text-gray-400'}`}>
                      {isDone ? <CheckCircle2 size={22} /> : <Icon size={20} />}
                    </div>
                    <span className={`text-xs mt-2 font-medium ${isActive ? 'text-orange-600' : isDone ? 'text-green-600' : 'text-gray-400'}`}>{s.title}</span>
                  </div>
                  {i < STEPS.length - 1 && (
                    <div className={`flex-1 h-1 mx-2 rounded-full transition-all duration-300 ${step > s.id ? 'bg-green-500' : 'bg-gray-200'}`} />
                  )}
                </React.Fragment>
              );
            })}
          </div>

          {/* Step Content */}
          <div className="bg-white rounded-2xl shadow-xl border border-gray-100 p-8 min-h-[320px]">
            <h3 className="text-lg font-semibold text-blue-900 mb-1">{STEPS[step - 1].label}</h3>
            <p className="text-sm text-gray-500 mb-6">Step {step} of {STEPS.length}</p>

            {step === 1 && (
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="step-1-categories">
                {CATEGORIES.map(cat => (
                  <button
                    key={cat}
                    onClick={() => set('productCategory', cat)}
                    className={`p-4 rounded-xl border-2 text-sm font-medium text-center transition-all duration-200 ${form.productCategory === cat ? 'border-orange-500 bg-orange-50 text-orange-700' : 'border-gray-200 hover:border-gray-300 text-gray-600'}`}
                    data-testid={`cat-${cat.toLowerCase().replace(/\s+/g, '-')}`}
                  >
                    {cat}
                  </button>
                ))}
                {form.productCategory === 'Other' && (
                  <div className="col-span-2 md:col-span-4 mt-2">
                    <Input value={form.customCategory} onChange={e => set('customCategory', e.target.value)} placeholder="Describe your product category..." className="border-gray-300" data-testid="custom-category-input" />
                  </div>
                )}
              </div>
            )}

            {step === 2 && (
              <div className="space-y-3" data-testid="step-2-volume">
                {VOLUMES.map(v => (
                  <button
                    key={v.value}
                    onClick={() => set('orderVolume', v.value)}
                    className={`w-full p-4 rounded-xl border-2 text-left text-sm font-medium transition-all duration-200 ${form.orderVolume === v.value ? 'border-orange-500 bg-orange-50 text-orange-700' : 'border-gray-200 hover:border-gray-300 text-gray-600'}`}
                    data-testid={`vol-${v.value.replace(/\s+/g, '-')}`}
                  >
                    {v.label}
                  </button>
                ))}
              </div>
            )}

            {step === 3 && (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3" data-testid="step-3-services">
                {SERVICES.map(s => (
                  <button
                    key={s.id}
                    onClick={() => toggleService(s.id)}
                    className={`p-4 rounded-xl border-2 text-left transition-all duration-200 ${form.services.includes(s.id) ? 'border-orange-500 bg-orange-50' : 'border-gray-200 hover:border-gray-300'}`}
                    data-testid={`svc-${s.id}`}
                  >
                    <span className={`text-sm font-semibold ${form.services.includes(s.id) ? 'text-orange-700' : 'text-gray-700'}`}>{s.label}</span>
                    <p className="text-xs text-gray-500 mt-1">{s.desc}</p>
                  </button>
                ))}
                <p className="text-xs text-gray-400 col-span-full mt-1">Select one or more services</p>
              </div>
            )}

            {step === 4 && (
              <div className="space-y-4" data-testid="step-4-contact">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <Label className="text-gray-700 font-medium text-sm">Contact Name *</Label>
                    <Input value={form.contactName} onChange={e => set('contactName', e.target.value)} placeholder="John Smith" required className="mt-1 border-gray-300" data-testid="wizard-contact-name" />
                  </div>
                  <div>
                    <Label className="text-gray-700 font-medium text-sm">Company Name</Label>
                    <Input value={form.companyName} onChange={e => set('companyName', e.target.value)} placeholder="Your Company Ltd." className="mt-1 border-gray-300" data-testid="wizard-company-name" />
                  </div>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <Label className="text-gray-700 font-medium text-sm">Email *</Label>
                    <Input type="email" value={form.email} onChange={e => set('email', e.target.value)} placeholder="contact@company.com" required className="mt-1 border-gray-300" data-testid="wizard-email" />
                  </div>
                  <div>
                    <Label className="text-gray-700 font-medium text-sm">Phone *</Label>
                    <Input type="tel" value={form.phone} onChange={e => set('phone', e.target.value)} placeholder="+1 234 567 8900" required className="mt-1 border-gray-300" data-testid="wizard-phone" />
                  </div>
                </div>
                <div>
                  <Label className="text-gray-700 font-medium text-sm">Country</Label>
                  <Input value={form.country} onChange={e => set('country', e.target.value)} placeholder="Your Country" className="mt-1 border-gray-300" data-testid="wizard-country" />
                </div>
                <div>
                  <Label className="text-gray-700 font-medium text-sm">Additional Notes</Label>
                  <Textarea value={form.message} onChange={e => set('message', e.target.value)} rows={3} placeholder="Any specific requirements, target prices, or questions..." className="mt-1 border-gray-300 resize-none" data-testid="wizard-message" />
                </div>
              </div>
            )}
          </div>

          {/* Navigation */}
          <div className="flex items-center justify-between mt-6">
            <Button
              variant="ghost"
              onClick={() => setStep(s => s - 1)}
              disabled={step === 1}
              className="text-gray-500 hover:text-gray-700"
              data-testid="wizard-back-btn"
            >
              <ArrowLeft className="mr-2 h-4 w-4" /> Back
            </Button>

            {step < 4 ? (
              <Button
                onClick={() => setStep(s => s + 1)}
                disabled={!canNext()}
                className="bg-orange-600 hover:bg-orange-700 disabled:opacity-40 text-white px-8 py-3 text-base font-semibold transition-all duration-200"
                data-testid="wizard-next-btn"
              >
                Next <ArrowRight className="ml-2 h-4 w-4" />
              </Button>
            ) : (
              <Button
                onClick={handleSubmit}
                disabled={!canNext() || isSubmitting}
                className="bg-orange-600 hover:bg-orange-700 disabled:opacity-40 text-white px-8 py-3 text-base font-semibold transition-all duration-200"
                data-testid="wizard-submit-btn"
              >
                {isSubmitting ? (
                  <><Loader2 className="mr-2 h-4 w-4 animate-spin" /> Submitting...</>
                ) : (
                  <><Send className="mr-2 h-4 w-4" /> Submit Quote Request</>
                )}
              </Button>
            )}
          </div>
        </div>
      </div>
    </section>
  );
};

export default QuoteForm;
